import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  Paper,
  alpha,
  useTheme,
  Stack
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { VpnKeyOutlined as VpnKeyOutlinedIcon } from "@mui/icons-material";
import axios from "axios";
import { Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { API_BASE_URL } from "../../../config/apiBase";
import { adminAuthHeaders, getApiErrorMessage } from "../../../utils/adminApi";
import { clearAdminSession, getAdminIdFromToken } from "../../../utils/adminSession";
import { adminScrollPageSx } from "../../../theme/adminLayout";

const ChangePassword = () => {
  const theme = useTheme();
  
  // Match the same blue color scheme from Dashboard
  const colors = {
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
    grey: {
      100: "#ffffff",
      200: "#f8f8f8",
      300: "#f0f0f0",
      400: "#e8e8e8",
      500: "#e0e0e0",
      600: "#cccccc",
      700: "#999999",
      800: "#666666",
      900: "#333333",
    }
  };

  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFormSubmit = async (values) => {
    const adminId = getAdminIdFromToken();
    if (!adminId) {
      setAlertSeverity("error");
      setAlertMessage("Admin session expired. Please log in again.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/ChangePassword/${adminId}`,
        {
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
        { headers: adminAuthHeaders() }
      );

      if (response.status === 200 && response.data.success !== false) {
        setAlertSeverity("success");
        setAlertMessage(response.data.message || "Password changed successfully.");
        setOpenSnackbar(true);
        setTimeout(() => {
          setOpenSnackbar(false);
          clearAdminSession();
          navigate("/");
        }, 2000);
      } else {
        setAlertSeverity("error");
        setAlertMessage(response.data.message || "Failed to change password. Please try again.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("API Error:", error);
      setAlertSeverity("error");
      setAlertMessage(getApiErrorMessage(error, "Failed to change password. Please try again."));
      setOpenSnackbar(true);
    }
  };

  const handleTogglePassword = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
    }
  };

  return (
    <Box sx={adminScrollPageSx}>
      {/* Header Section - Using same gradient as Dashboard */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <VpnKeyOutlinedIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Change Password
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Update your account password securely
        </Typography>
      </Box>

      {/* Form Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          borderRadius: 2,
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box display="grid" gap="20px">
                <TextField
                  fullWidth
                  variant="filled"
                  type={showCurrentPassword ? "text" : "password"}
                  label="Current Password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.currentPassword}
                  name="currentPassword"
                  error={!!touched.currentPassword && !!errors.currentPassword}
                  helperText={touched.currentPassword && errors.currentPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('current')}
                          edge="end"
                        >
                          {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type={showNewPassword ? "text" : "password"}
                  label="New Password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.newPassword}
                  name="newPassword"
                  error={!!touched.newPassword && !!errors.newPassword}
                  helperText={touched.newPassword && errors.newPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('new')}
                          edge="end"
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type={showConfirmPassword ? "text" : "password"}
                  label="Confirm Password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmPassword}
                  name="confirmPassword"
                  error={!!touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('confirm')}
                          edge="end"
                        >
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" mt="20px">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: colors.blueAccent[500],
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    '&:hover': {
                      backgroundColor: colors.blueAccent[600],
                    }
                  }}
                >
                  Update Password
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          sx={{
            backgroundColor: alertSeverity === 'success' ? '#4caf50' : '#f44336',
            color: 'white'
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  currentPassword: yup.string().required("Required"),
  newPassword: yup
    .string()
    .required("Required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_+=\-/\[\]`~\\';])[A-Za-z\d!@#$%^&*(),.?":{}|<>_+=\-/\[\]`~\\';]{8,}$/,
      "Must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Passwords must match")
    .required("Required")
});

const initialValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export default ChangePassword;